import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SchoolProgramService } from '../../../../shared/services/school-program.service';
import { SchoolAndLocationService } from '../../../../shared/services/school-and-location.service';
import { ResponseService } from '../../../../shared/services/response-service.service';
import SelectColumnType from '@revolist/revogrid-column-select';
import { AutoAddRowsPlugin } from '@revolist/revogrid';
import {
  allowRevoGridFilterInputTyping,
  commitRevoGridEdit,
  firstEditableColumn,
  focusRevoGridRow,
  isEmptyValue,
  rowActionButton,
  seedNewRows,
  selectCellTemplate,
  syncRevoGridSource,
  withValidationStyles,
} from '../../../../shared/utils/revo-grid';
import { TranslateService } from 'src/shared/services/translate.service';

const REQUIRED_PROPS = ['school_location_id', 'school_program_name'];

interface ProgramRow {
  school_program_id?: number | null;
  school_location_id: any;
  school_program_name: string;
  school_program_description: string;
  changed: boolean;
}

@Component({
  selector: 'app-school-program-grid',
  templateUrl: './school-program-grid.component.html',
  styleUrls: ['./school-program-grid.component.scss'],
})
export class SchoolProgramGridComponent implements OnInit {
  @ViewChild('programGrid', { read: ElementRef }) gridRef?: ElementRef<HTMLElement>;

  loading = true;
  saving = false;


  locationOptions: any[] = [];
  columns: any[] = [];
  source: ProgramRow[] = [];

  columnTypes = {
    select: new SelectColumnType(),
  };

  clipboardConfig = {
    rangeFill: true,
  };

  gridPlugins = [AutoAddRowsPlugin];

  private invalidCells = new Map<ProgramRow, Set<string>>();

  constructor(
    public activeModal: NgbActiveModal,
    private schoolProgramService: SchoolProgramService,
    private salService: SchoolAndLocationService,
    private rpSer: ResponseService,
    private translate: TranslateService,
  ) {
    this.translate.load('extracurricular', 'activity');
  }

  onGridKeydown = allowRevoGridFilterInputTyping;

  ngOnInit(): void {
    this.loadData();
  }

  private get gridElement(): any {
    return this.gridRef?.nativeElement ?? null;
  }

  onNewRows(event: Event): void {
    if (this.saving) {
      event.preventDefault();
      return;
    }
    seedNewRows(event as CustomEvent<{ newRows: { index: number; data: any }[] }>, () => this.createEmptyRow());
  }

  private loadData(): void {
    this.loading = true;

    this.salService.getSchoolLocations().subscribe(
      (response: any) => {
        if (response['error'] === 0) {
          const locations = response['result']['data'] ?? [];
          this.locationOptions = locations.map((loc: any) => ({
            value: loc.school_location_id,
            label: loc.school_short_address,
          }));
        }

        this.buildColumns();
        this.loadPrograms();
      },
      () => {
        this.rpSer.showAddError();

        this.locationOptions = [];
        this.buildColumns();
        this.loadPrograms();
      },
    );
  }

  private loadPrograms(): void {
    this.schoolProgramService.getSchoolProgram({}).subscribe(
      (response: any) => {
        if (response.status) {
          this.source = (response.data ?? []).map((item: any) => ({
            school_program_id: item.school_program_id ?? null,
            school_location_id: item.school_location_id ?? null,
            school_program_name: item.school_program_name ?? '',
            school_program_description: item.school_program_description ?? '',
            changed: false,
          }));
        } else {
          this.source = [];
        }
        this.loading = false;
      },
      () => {
        this.source = [];
        this.loading = false;
      },
    );
  }

  private buildColumns(): void {
    const locationLabels = new Map(
      this.locationOptions.map((location) => [
        String(location.value),
        location.label,
      ]),
    );
    const isSaved = (row: ProgramRow) => row.school_program_id != null;

    const dataColumns = [
      {
        prop: 'school_location_id',
        name: 'School Location',
        size: 260,
        sortable: true,
        filter: 'string',
        readonly: (props: any) => isSaved(props.model),
        columnType: 'select',
        labelKey: 'label',
        valueKey: 'value',
        syncCellTemplate: false,
        source: this.locationOptions,
        cellTemplate: selectCellTemplate(
          (model: ProgramRow) => locationLabels.get(String(model.school_location_id)) ?? '',
          isSaved,
          'School Location',
        ),
        cellCompare: (_prop: string, a: ProgramRow, b: ProgramRow) => {
          const aLabel = locationLabels.get(String(a.school_location_id)) ?? '';
          const bLabel = locationLabels.get(String(b.school_location_id)) ?? '';

          return aLabel.localeCompare(bLabel, undefined, {
            sensitivity: 'base',
          });
        },
        disabled: true
      },
      {
        prop: 'school_program_name',
        name: 'School Program Name',
        size: 260,
        sortable: true,
        filter: 'string',
      },
      {
        prop: 'school_program_description',
        name: 'School Program Description',
        size: 360,
        sortable: false,
        filter: 'string',
      },
    ];

    this.columns = [
      ...withValidationStyles(dataColumns, REQUIRED_PROPS, (model, prop) => !!this.invalidCells.get(model)?.has(prop)),
      {
        prop: 'actions',
        name: '',
        size: 60,
        sortable: false,
        readonly: true,
        cellTemplate: (h: any, props: any) => {
          const row = props.model as ProgramRow;

          if (row.school_program_id != null) {
            return '';
          }

          return rowActionButton(h, 'delete', 'Remove', () => this.onRemoveRow(row));
        },
      },
    ];
  }

  async onAddRow(): Promise<void> {
    if (this.saving) return;
    this.source = await syncRevoGridSource(this.gridElement, this.source);
    const row = this.createEmptyRow();
    this.source = [...this.source, row];
    await focusRevoGridRow(this.gridElement, row, firstEditableColumn(this.columns, row));
  }

  async onRemoveRow(row: ProgramRow): Promise<void> {
    if (row.school_program_id != null) {
      return;
    }

    this.source = await syncRevoGridSource(this.gridElement, this.source);
    this.invalidCells.delete(row);
    this.source = this.source.filter((currentRow) => currentRow !== row);
  }

  onAfterEdit(event: CustomEvent<any>): void {
    if (event.detail.model) {
      event.detail.model.changed = true;
    }

    (Object.values(event.detail.models ?? {}) as ProgramRow[]).forEach((row) => {
      if (row) {
        row.changed = true;
      }
    });

    const invalidRows = [event.detail.model, ...Object.values(event.detail.models ?? {})].filter(
      (row: ProgramRow) => row && this.invalidCells.has(row),
    ) as ProgramRow[];
    if (invalidRows.length) {
      invalidRows.forEach((row) => {
        const props = this.invalidProps(row);
        if (props.length) {
          this.invalidCells.set(row, new Set(props));
        } else {
          this.invalidCells.delete(row);
        }
      });
      this.refreshGrid();
    }
  }

  private createEmptyRow(): ProgramRow {
    return {
      school_program_id: null,
      school_location_id: null,
      school_program_name: '',
      school_program_description: '',
      changed: false,
    };
  }

  private invalidProps(row: ProgramRow): string[] {
    return REQUIRED_PROPS.filter((prop) => isEmptyValue((row as any)[prop]));
  }

  private markInvalidCells(rows: ProgramRow[]): void {
    this.invalidCells = new Map(
      rows
        .map((row) => [row, new Set(this.invalidProps(row))] as [ProgramRow, Set<string>])
        .filter(([, props]) => props.size),
    );
    this.refreshGrid();
  }

  private refreshGrid(): void {
    this.gridElement?.refresh?.('all')?.catch?.(() => undefined);
  }

  private isRowFilled(row: ProgramRow): boolean {
    return !!(
      row.school_location_id ||
      (row.school_program_name && row.school_program_name.trim()) ||
      (row.school_program_description && row.school_program_description.trim())
    );
  }

  async onSave(): Promise<void> {
    if (this.saving) return;
    commitRevoGridEdit(this.gridElement);
    this.source = await syncRevoGridSource(this.gridElement, this.source);
    const filledRows = this.source.filter((row) => this.isRowFilled(row));
    this.markInvalidCells(filledRows);
    const programs = filledRows
      .map((row) => ({
        school_program_id: row.school_program_id ?? null,
        school_location_id: +row.school_location_id,
        school_program_name: (row.school_program_name ?? '').trim(),
        school_program_description: (row.school_program_description ?? '').trim(),
        changed: row.changed,
      }));

    const invalid = programs.find(
      (p) => !p.school_location_id || !p.school_program_name,
    );
    if (programs.length === 0) {
      this.rpSer.showAddError('There is nothing to save.');
      return;
    }
    if (invalid) {
      this.rpSer.showAddError(
        'Each row needs a school location and a program name.',
      );
      return;
    }

    this.saving = true;
    this.schoolProgramService.bulkSaveSchoolProgram(programs).subscribe(
      (response: any) => {
        this.saving = false;
        if (response.status) {
          this.rpSer.showAddSuccess(response.message);
          this.activeModal.close(programs);
        } else {
          this.rpSer.showAddError(response.message);
        }
      },
      () => {
        this.saving = false;
        this.rpSer.showAddError();
      },
    );
  }

  onClose(): void {
    this.activeModal.dismiss();
  }
}
